import { useFormik } from "formik";
import React, { useState } from "react";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import Step1 from "../components/CreateProject/steps/Step1";
import Step2 from "../components/CreateProject/steps/Step2";
import Step3 from "../components/CreateProject/steps/Step3";
import Container from "../components/common/Container";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import {
  useEditProjectMutation,
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  usePublishProjectMutation,
  useSaveProjectMutation,
  useSendInvitationEmailMutation,
} from "../redux/slices/api/projectsApiSlice";
import notify from "../components/common/notify";
import { handleError } from "../hooks/useApi";
import { Bars, Oval } from "react-loader-spinner";
import { useEffect } from "react";
import { addTimeToDate, stringToISO } from "../utils/dateTool";
import useDetectFieldError from "../hooks/useDetectFieldError";
import { useRef } from "react";
import { projectFieldsByStep, projectSteps } from "../Constants/project";
import { scroller } from "react-scroll";
import CircleStep from "../components/common/CircleStep";
import { useMemo } from "react";
import {
  useGetNotificationsQuery,
  useLazyGetUserQuery,
} from "../redux/slices/api/userApi";
import useResetForm from "../hooks/useResetForm";
import { EDITOR_LIMITS, URL_REGEX } from "../Constants";
import ExternalLink from "../components/common/ExternalLink";
import { convertHtmlToText } from "../utils/general";
import { useSelector } from "react-redux";
import { selectGeneral } from "../redux/slices/generalSlice";
import useDiscardProject from "../hooks/useDiscardProject";
import ProjectPreview from "../components/projects/ProjectPreview";
import MetaTags from "../components/common/MetaTags";
import { useChainId, useChains } from "wagmi";

const ChangeStepButton = ({ type = "next", label, onClick, className }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex text-custom-gray-800 font-medium md:text-lg ${
        type === "next" ? "pl-5" : "pr-5"
      } group hover:text-primary items-center min-w-fit md:w-36 h-fit ${className} `}
    >
      {type === "prev" && (
        <BsChevronLeft className="text-custom-gray-800 mr-1 group-hover:text-primary" />
      )}
      {label ?? (type === "next" ? "Next Step" : "Prev Step")}
      {type === "next" && (
        <BsChevronRight className="text-custom-gray-800 ml-1 group-hover:text-primary" />
      )}
    </button>
  );
};
const CreateProject = () => {
  // Navigation utility hook
  const navigate = useNavigate();
  
  // Get current chain using Wagmi v2 hooks
  const chainId = useChainId();
  const chains = useChains();
  const chain = chains?.find((c) => c.id === chainId); 

  // State for managing URL search parameters
  const [searchParam, setSearchParam] = useSearchParams();
  let edit_id_param = searchParam.get("edit");

  // State management for project creation/editing steps and status
  const [activeStep, setActiveStep] = useState(0);
  const [edit_id, setEdit_id] = useState(edit_id_param);
  const [isSaved, setIsSaved] = useState(false);
  const [isPublish, setIsPublish] = useState(false);
  const [saveAndPublishLoading, setSaveAndPublishLoading] = useState(false);
  const [showProjectPreview, setShowProjectPreview] = useState(false);
  const [projectPreviewData, setProjectPreviewData] = useState(null);

  // Effect to update 'edit_id' when search parameters change
  useEffect(() => {
    setEdit_id(edit_id_param);
  }, [edit_id_param]);

  // Query project data based on 'edit_id' when in edit mode
  const { isLoading: projectDataLoading, data: projectData } =
    useGetProjectByIdQuery({ id: edit_id_param }, { skip: !edit_id_param });

  // Lazy query to fetch project data triggered manually
  const [triggerGetProjectData] = useLazyGetProjectByIdQuery();

  // Lazy query to fetch user data triggered manually
  const [triggerGetUser] = useLazyGetUserQuery();

  // Selector for general state management
  const { isLoggedIn } = useSelector(selectGeneral);

  // Query to fetch notifications when the user is logged in
  const { refetch: refetchNotifications } = useGetNotificationsQuery(undefined, {
    skip: !isLoggedIn,
  });



  // Function to filter out empty subcategories in project budgets
  const filteredBudgets = (budgets = []) => {
    // Filter and sanitize budgets, removing subcategories with no name or budget
    let budgetsClone = [...budgets];
    budgetsClone = budgetsClone.map((budget, index) => {
      const { category, subCategory } = budget;
      let filteredSubCategory = subCategory.filter(
        (sub) => sub.name.length > 0 && sub.budget > 0
      );
      return { category, subCategory: filteredSubCategory };
    });
    return budgetsClone?.filter((item) => item?.subCategory?.length);
  };

  // Function to filter out empty tasks and sanitize due dates in project timeline
  const filteredTimeline = (timeline = []) => {
    // Filter and sanitize timeline tasks, removing those with no name and formatting due dates
    let timelineClone = [...timeline];
    timelineClone = timelineClone.map((task, index) => {
      let due_date = stringToISO(task.due_date);
      return { ...task, due_date };
    });
    let filteredTimeline = timelineClone
      .filter((sub) => sub?.name?.length > 1)
      .map((item) => {
        let { detail, ...rest } = item;
        return { ...rest, ...(detail?.trim() && { detail }) };
      });

    return filteredTimeline;
  };

  // Function to set project fields when in edit mode
  const setProjectFields = () => {
    if (edit_id) {
      let { project } = { ...projectData };
      let keywords = project?.keywords?.map((keyword) => ({
        id: keyword,
        text: keyword,
      }));

      let collaborators_queue = project?.collaborators_queue?.map((item) => {
        const { _id, requestedAt, sentTimes } = item ?? {};
        if (!!_id) return { ..._id, requestedAt, sentTimes };
        else return item?.email;
      });
      let timeline = filteredTimeline(project?.timeline);
      let budgets = filteredBudgets(project?.budget_allocation);
      formik.setValues({
        ...formik?.values,
        ...project,
        keywords,
        collaborators_queue,
        termsOfService: project?.visible,
        timeline,
        budgets,
      });
    }
  };

  // Mutation functions for saving, publishing, editing, and discarding a project
  const [saveProject, { isLoading: saveDraftLoading }] =
    useSaveProjectMutation();
  const [publishProject, { isLoading: publishLoading }] =
    usePublishProjectMutation();
  const [editProject, { isLoading: editLoading }] = useEditProjectMutation();
  const [sendEmailInvitation, { isLoading: sendEmailInvitationLoading }] =
    useSendInvitationEmailMutation();
  const { isLoading: discardProjectLoading, handleDiscardProject } =
    useDiscardProject();

  // Effect to set project fields when project data is loaded
  useEffect(() => {
    if (!projectDataLoading) {
      setProjectFields();
    }
    //eslint-disable-next-line
  }, [projectDataLoading, projectData]);

  const formik = useFormik({
    initialValues: {
      title: "",
      category: "",
      keywords: [],
      links: [""],
      abstract: "",
      summary: "",
      fundraising_method: "Crowdfunding",
      termsOfService: false,
      beneficiaries: "",
      equipment: "",
      project_duration: "",
      royalties: "",
      attachment: "",
      total_budget: 0,
      funding_goal: "",
      minimum_offer: "",
      no_minimum_offer: false,
      total_duration: 0,
      blockchain: "bnb",
      token: "BNB",
      collaborators_queue: [],
      collaborators: [],
      collaborators_email: [],
      all_collaborators: [],
      timeline: [
        {
          name: "",
          start_date: undefined,
          due_date: undefined,
          duration: 1,
          detail: "",
        },
      ],

      budgets: [
        {
          category: "Personnel",
          subCategory: [
            {
              name: "Researcher Salary",
              detail: "",
              budget: 0.1,
            },
          ],
        },
      ],
    },
    onSubmit: (values) => {
      handleSubmit(values);
    },
    // validateOnBlur: true,
    validateOnChange: true,
    validationSchema: yup.object().shape({
      collaborators: yup.array().of(yup.string()),
      title: yup
        .string()
        .required("title is required")
        .min(4, "title can not be lower than 4 characters")
        .max(200, "title can not be more than 200 characters"),
      abstract: yup
        .string()
        .required("abstract is required")
        .matches(URL_REGEX, "image link must be a valid url"),
      category: yup.string().required("category is required"),

      keywords: yup
        .array()
        .required("keywords are required")
        .min(1, "keywords must have at least 1 item"),
      links: yup
        .array()
        .of(
          yup
            .string()
            .matches(URL_REGEX, "link items must be a valid url")
            .min(5, "link items can not be lower than 5 characters")
            .max(100, "link items can not be more than 100 characters")
        ),
      summary: yup
        .string()
        .required("summary is required!")
        .min(10, "summary can not be lower than 10 characters")
        .max(
          EDITOR_LIMITS.summary,
          `summary can not be more than ${EDITOR_LIMITS.summary} characters`
        )
        .test(
          "is-lower-than-50-characters",
          "summary can not be lower than 10 characters",
          function (value, context) {
            // let summary = convertHtmlToText({ htmlContent: value });
            // if (summary?.length < 10) return false;
            return true;
          }
        ),
      beneficiaries: yup
        .string()
        .max(
          EDITOR_LIMITS.beneficiaries,
          `beneficiaries can not be more than ${EDITOR_LIMITS.beneficiaries} characters`
        ),
      equipment: yup
        .string()
        .max(
          EDITOR_LIMITS.equipment,
          `equipment can not be more than ${EDITOR_LIMITS.equipment} characters`
        ),
      timeline: yup
        .array()
        .required("timeline is required")
        .of(
          yup
            .object()
            .shape({
              name: yup
                .string()
                .required("timeline names are required")
                .min(2, "The minimum name character is 2.")
                .max(200, "The maximum name character is 200"),

              due_date: yup
                .date()
                .test(
                  "is-greater-than-next",
                  "Invalid due date!",
                  function (value, context) {
                    let index = Number(context.path.match(/\d+/));
                    let timeline = context?.from?.[1].value?.timeline;
                    let next_due_date = timeline?.[index + 1]?.due_date ?? 0;
                    let current_due_date = timeline?.[index]?.due_date ?? 0;

                    if (
                      Number(index) + 1 < Number(timeline?.length) &&
                      new Date(current_due_date) > new Date(next_due_date)
                    )
                      return false;
                    return true;
                  }
                )
                .required("Due date is required"),
              duration: yup
                .number()
                .required("timeline durations are required")
                .min(0, "timeline durations can not be lower than 0"),
              detail: yup
                .string()
                .min(2, "timeline details can not be lower than 2 characters")
                .max(
                  300,
                  "timeline details can not be more than 300 characters"
                ),
            })
            .noUnknown(true)
        )
        .min(1, "timeline must have at least one item"),

      budgets: yup
        .array()
        .required("budgets is required")
        .of(
          yup.object().shape({
            category: yup.string().required("category name is required"),
            subCategory: yup
              .array()
              .required("sub category is required")
              .of(
                yup.object().shape({
                  name: yup
                    .string()
                    .required("budget name is required")
                    .max(50, "budget name can not be more than 50 characters"),
                  detail: yup
                    .string()
                    .max(50, "detail can not be more than 50 characters"),
                  budget: yup
                    .number()
                    .min(0.0001, "budget minimum is 0.0001")
                    .required("budget amount is required"),
                })
              ),
          })
        )
        .min(1, "budget must have at least one item."),

      total_duration: yup
        .number()
        .min(0, "total duration can not be lower than 0"),
      total_budget: yup.number().min(0, "total budget can not be lower than 0"),
      funding_goal: yup
        .number()
        .when("fundraising_method", ([fundraising_method], schema) => {
          if (fundraising_method === "Crowdfunding")
            return yup
              .number()
              .required("funding goal is required")
              .min(0, "funding goal can not be lower than 0");
          return schema;
        }),

        fundraising_method: yup
        .string()
        .required("fundraising method is required")
        .oneOf(
          ["Crowdfunding"],
          "fundraising method is invalid!"
        ),

      no_minimum_offer: yup.boolean(),
      minimum_offer: yup
        .number()
        .when("no_minimum_offer", {
          is: true,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.required("minimum offer is required").min(0, "minimum offer can not be lower than 0"),
        }),

      project_duration: yup.number().required("project duration is required"),

      attachment: yup
        .string()
        .matches(URL_REGEX, "attachment must be a valid url")
        .min(30, "attachment url can not be lower than 30 characters")
        .max(150, "attachment url can not be more than 150 characters"),

      blockchain: yup.string().required("blockchain field is required!"),
      token: yup.string().required("token field is required!"),
      termsOfService:
        projectData?.project?.visible || !isPublish
          ? yup.boolean()
          : yup
              .boolean()
              .required("The terms and conditions must be accepted.")
              .oneOf([true], "The terms and conditions must be accepted."),
    }),
  });

  // Handle form submission, triggering save and publish project based on conditions
  const handleSubmit = (values) => {
    handleSaveAndPublishProject(!projectData?.project?.visible && isPublish);
  };

  // Ref for tracking if form validation has occurred on mount
  const validateOnMountRef = useRef(false);
  useEffect(() => {
    if (validateOnMountRef?.current) return;
    validateOnMountRef.current = true;

    // Validate the form on mount
    formik?.validateForm().then();
  }, []);

  // State and utility hook for managing step animations and error scrolling
  const [stepClassname, setStepClassname] = useState("");
  const { scrollToError } = useDetectFieldError({
    formik,
  });
  const showSteps = [
    <Step1 formik={formik} containerClass={stepClassname} />,
    <Step2 formik={formik} containerClass={stepClassname} />,
    <Step3 formik={formik} containerClass={stepClassname} />,
  ];

  // Scroll to top component for step transitions
  const ScrollToTop = () => {
    window.scrollTo(0, 0);
    return null;
  };

  // Handlers for moving between project creation/edit steps
  const handlePrevStep = () => {
    setActiveStep((prev) => prev - 1);
    setStepClassname("prev-step-animation");
    ScrollToTop();
  };
  const handleNextStep = () => {
    // Check for form errors before advancing to the next step
    // and handle error scrolling and step transitions
    // based on the active step and form validation
    let currentErrors = projectFieldsByStep?.[activeStep];
    let errorKeys = Object.keys(formik?.errors);
    let isError = () =>
      !formik?.isValid &&
      (errorKeys?.some((item) => currentErrors?.includes(item)) ||
        currentErrors?.some((item) => item?.includes(errorKeys)));
    if (isError()) {
      let touchedFields = currentErrors.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      formik?.setTouched(touchedFields);
      scrollToError({ activeStepFields: currentErrors });
    } else {
      setActiveStep((prev) => prev + 1);
      setStepClassname("next-step-animation");
      ScrollToTop();
    }
  };

  // Reset form to initial values on mount using custom hook
  useResetForm({ onMount: () => formik.setValues(formik.initialValues) });

  // Function to check and handle form field errors during form submission
  const checkFieldsError = (props) => {
    let { isDraft = false } = props ?? {};
    formik?.setFieldTouched("abstract", true);
    formik?.setFieldTouched("funding_goal", true);
    let errorKeys = Object.keys(formik?.errors);
    if (isDraft) {
      let touchedFields = errorKeys?.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      formik?.setFieldTouched(touchedFields, true);
    }
    const firstKey = errorKeys?.[0];

    let foundFieldStep = projectFieldsByStep.findIndex((item) =>
      item?.includes(firstKey)
    );
    if (foundFieldStep >= 0) {
      if (foundFieldStep > activeStep) {
        setStepClassname("next-step-animation");
        setActiveStep(foundFieldStep);
      } else if (foundFieldStep < activeStep) {
        setStepClassname("back-step-animation");
        setActiveStep(foundFieldStep);
      }
      setTimeout(() => {
        scroller.scrollTo(firstKey, {
          duration: 300,
          delay: 0,
          smooth: "easeInOutQuint",
          offset: -160,
        });
      }, 200);
      return;
    }
  };
  useEffect(() => {
    // Check and handle form field errors during form submission
    if (!formik.isValid && formik.submitCount !== 0 && formik.isSubmitting) {
      checkFieldsError();
    }
  }, [formik.submitCount, formik.isSubmitting]);

  // Function to calculate the total budget of the project
  const calcTotalBudget = () => {
    let totalBudgets = [...formik.values.budgets];
    let totalBudget = 0;
    totalBudgets.forEach((category) => {
      category.subCategory.forEach((subCategory) => {
        totalBudget += Number(subCategory.budget);
      });
    });
    return totalBudget.toFixed(6);
  };
  const calcTotalDuration = () => {
    let timelineClone = [...formik.values.timeline];
    let totalDuration = timelineClone.reduce((acc, task) => {
      return acc + task.duration;
    }, 0);
    return totalDuration;
  };
  const handleSendEmail = (id) => {
    let { collaborators_email } = formik?.values ?? {};
    return new Promise(async (resolve, reject) => {
      if (!collaborators_email?.length) return resolve(true);
      try {
        let res = await sendEmailInvitation({
          body: { emails: collaborators_email },
          id,
        }).unwrap();

        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  };

  const loadingStates = useMemo(
    () => ({
      saveDraftLoading,
      editLoading,
      publishLoading,
      saveAndPublishLoading,
      sendEmailInvitationLoading,
    }),
    [
      saveDraftLoading,
      editLoading,
      publishLoading,
      saveAndPublishLoading,
      sendEmailInvitationLoading,
    ]
  );

  const loadingTexts = {
    saveDraftLoading: "Saving...",
    editLoading: "Editing...",
    publishLoading: "Publishing...",
    saveAndPublishLoading: "Publishing...",
  };

  const submitLoading = useMemo(() => {
    for (let state in loadingStates) {
      if (loadingStates?.[state]) {
        return {
          isLoading: true,
          text: loadingTexts?.[state] || "Waiting ...",
        };
      }
    }

    return { isLoading: false, text: "" };
  }, [loadingStates, projectData?.project]);

  // Function to handle saving and publishing a project
  const handleSaveAndPublishProject = async (isPublish) => {
    // Declaration of variables to construct the request body
    let body = {};
    let data = {};

    // Extracting keyword IDs
    let keywords = formik.values.keywords;
    keywords = keywords?.map((item) => item.id);

    // Filtering non-empty links
    let links = formik.values.links.filter((link) => link?.length);

    // Filtering and mapping collaborator queue IDs
    let collaborators_queue =
      formik?.values?.collaborators_queue
        ?.filter((item) => item?._id)
        .map((item) => item?._id && item?._id) ?? [];

    // Combining collaborator IDs
    let collaborators = [
      ...formik.values?.collaborators?.map((item) => item && item?._id),
      ...collaborators_queue,
    ];

    // Filtering the timeline
    let timeline = filteredTimeline(formik.values.timeline);

    // Filtering budgets
    let budgets = filteredBudgets(formik.values.budgets);

    // Keys to be excluded in constructing the request body
    let excludeKeys = [
      "createdAt",
      "updatedAt",
      "_id",
      "__v",
      "keywords",
      "budgets",
      "state",
      "termsOfService",
      "links",
      "collaborators_queue",
      "collaborators_email",
      "all_collaborators",
      "isOnAuction",
      "visible",
      "creator",
      "nft_details",
      "timeline",
      "blockchain",
      "token",
    ];

    // Fields to be converted to numbers
    const ConvertToNumberFields = ["project_duration", "minimum_offer"];

    // Iterate through form values and construct the request body
    for (const [key, value] of Object.entries(formik.values)) {
      let projectKeyValue = projectData?.project?.[key];
      let isVisible = formik.values.visible;
      let isValueDefined = value || value === false || value === 0 || value.length;
      let isKeyExcluded = excludeKeys.includes(key);
      let isValueChanged = !isVisible ? projectKeyValue !== value : true;

      if (isValueDefined && !isKeyExcluded && isValueChanged) {
        if (ConvertToNumberFields?.includes(key))
          data[key] = Number(value); // Convert certain fields to numbers
        else data[key] = value;
      }
    }

    // If no_minimum_offer is true, clear minimum_offer
    if (formik.values.no_minimum_offer) {
      data.no_minimum_offer = true;
      data.minimum_offer = 0;
    }

    // Construct the final body object
    body = {
      ...data,
      ...(keywords?.length && { keywords }),
      ...(budgets?.length && {
        budget_allocation: budgets,
        total_budget: calcTotalBudget(),
      }),
      total_duration: calcTotalDuration(),
      ...(timeline?.length && {
        timeline,
      }),
      links,
      collaborators,
    };

    /// ..... continuation of previous code ........ ///

    let saveRes;
    let editRes;

    try {
      // Check if the project is being published
      if (isPublish) {
        // Set loading state for save and publish
        setSaveAndPublishLoading(true);

        // If editing an existing project, send email and update the project
        if (edit_id) {
          await handleSendEmail(edit_id);
          editRes = await editProject({ body, id: edit_id }).unwrap();
        } else {
          // If creating a new project, save the project and send email
          saveRes = await saveProject({ body }).unwrap();
          await handleSendEmail(saveRes?.project_id);
        }

        // Set project as saved
        setIsSaved(true);

        // Publish the project
        let publishResponse = await publishProject({
          id: edit_id || saveRes?.project_id,
        }).unwrap();

        // Notify the user of the publishing success
        notify(publishResponse?.message, "success");

        // Fetch the updated project data
        await triggerGetProjectData({
          id: edit_id || saveRes?.project_id,
        }).unwrap();

        // Refetch notifications and navigate to the project summary page
        refetchNotifications();
        return navigate(`/projects/${saveRes?.project_id || edit_id}`);
      } else {
        // If not publishing, handle editing or saving the project
        if (edit_id) {
          await handleSendEmail(edit_id);
          editRes = await editProject({ body, id: edit_id }).unwrap();
          // Notify the user of the editing success
          notify(editRes?.message, "success");
          // Navigate to the project summary page
          navigate(`/projects/${saveRes?.project_id || edit_id}`);
        } else {
          saveRes = await saveProject({ body }).unwrap();
          await handleSendEmail(saveRes?.project_id);
          // Notify the user of the saving success
          notify(saveRes?.message, "success");
          // Navigate to the project summary page
          navigate(`/projects/${saveRes?.project_id || edit_id}`);
        }
      }
    } catch (error) {
      // Handle errors during the saving or publishing process
      handleError(error, null, true);
    }

    // Refetch notifications, reset collaborators_email field, and update loading states
    refetchNotifications();
    await triggerGetUser().unwrap();
    formik?.setFieldValue("collaborators_email", []);
    setSaveAndPublishLoading(false);
    setStepClassname("");
    formik.setFieldValue("termsOfService", false);

    // If a new project was saved, update edit_id and URL parameters
    if (saveRes?.project_id) {
      searchParam.set("edit", saveRes?.project_id);
      searchParam.delete("new");
      setSearchParam(searchParam);
      setEdit_id(saveRes?.project_id);
    }
  };

  const handleClickStep = (step) => {
    if (step < activeStep) setStepClassname("prev-step-animation");
    else setStepClassname("next-step-animation");
    setActiveStep(step);
  };

  const handleProjectPreviewClick = () => {
    setShowProjectPreview(true);
    const values = formik.values;
    const keywords = values?.keywords?.map((item) => item.id);

    const data = { ...values, keywords };
    setProjectPreviewData(data);
  };
  if (!isSaved && edit_id && projectDataLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div className="w-full flex flex-col pt-24 mlg:pt-32 items-center roboto-font ">
        <MetaTags
          title={edit_id ? "Edit Project" : "Create Project"}
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link="/create-project"
          noIndex
        />
        <div className="w-full flex flex-col items-center justify-center pb-8 mb-8 mlg:mb-20 bg-custom-gray-100">
          <Container className="flex justify-between items-center">
            {activeStep > 0 ? (
              <ChangeStepButton
                type="prev"
                onClick={handlePrevStep}
                className="!hidden mlg:!flex"
              />
            ) : (
              <span className="w-24 md:min-w-[144px] hidden mlg:block"></span>
            )}
            {/* /// steper // */}

            <div className="w-full lg:w-9/12 flex items-center justify-center">
              {projectSteps.map((item, index, stepsArray) => (
                <CircleStep
                  label={item}
                  key={index}
                  stepLength={stepsArray.length}
                  onClick={() => handleClickStep(index)}
                  stepIndex={index}
                  activeStep={activeStep}
                  circleClass="w-8 h-8 mlg:w-10 mlg:h-10"
                  buttonCLass="w-24 mlg:w-32"
                />
              ))}
            </div>

            {/* ///////////// */}
            {activeStep < 2 ? (
              <ChangeStepButton
                type="next"
                onClick={handleNextStep}
                className="!hidden mlg:!flex"
              />
            ) : (
              <span className="w-24 md:min-w-[144px] hidden mlg:block"></span>
            )}
          </Container>
          <div className="w-full flex mlg:hidden justify-between mt-5 px-7 ">
            {activeStep > 0 ? (
              <ChangeStepButton type="prev" onClick={handlePrevStep} />
            ) : (
              <span className="w-24 md:min-w-[144px]"></span>
            )}

            {/* ///////////// */}
            {activeStep < 2 ? (
              <ChangeStepButton type="next" onClick={handleNextStep} />
            ) : (
              <span className="w-24 md:min-w-[144px]"></span>
            )}
          </div>
        </div>
        <Container>
          <form
            onSubmit={formik.handleSubmit}
            className="w-full flex flex-col pb-20 min-h-screen"
          >
            {showSteps[activeStep]}

            <div className="w-full flex justify-between py-8 mt-3 mlg:mt-10 ">
              {activeStep > 0 ? (
                <ChangeStepButton type="prev" onClick={handlePrevStep} />
              ) : (
                <span className="w-24 md:min-w-[144px]"></span>
              )}

              {/* ///////////// */}
              {activeStep < 2 ? (
                <ChangeStepButton type="next" onClick={handleNextStep} />
              ) : (
                <span className="w-24 md:min-w-[144px]"></span>
              )}
            </div>
            <div className="w-full flex flex-col mlg:flex-row items-start gap-5 justify-between mt-6">
              <div className="max-mlg:w-full flex justify-between items-center gap-x-5">
                {!projectData?.project?.visible && (
                  <button
                    type="button"
                    onClick={() => handleSaveAndPublishProject()}
                    disabled={
                      editLoading || saveDraftLoading || saveAndPublishLoading
                    }
                    className={` font-medium text-xl mlg:text-2xl px-5 save-draft-btn rounded-xl h-fit mt-1 `}
                  >
                    {!saveAndPublishLoading &&
                    (editLoading ||
                      saveDraftLoading ||
                      sendEmailInvitationLoading) ? (
                      <Oval
                        height={35}
                        width={35}
                        color="#8538FF"
                        wrapperStyle={{}}
                        wrapperClass=""
                        visible={true}
                        ariaLabel="oval-loading"
                        secondaryColor="#8538FF"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                      />
                    ) : edit_id ? (
                      " Save Changes"
                    ) : (
                      " Save Draft"
                    )}
                  </button>
                )}
                {!projectData?.project?.visible &&
                  projectData &&
                  !projectData?.project?.nft_details?.address && (
                    <button
                      type="button"
                      onClick={() => handleDiscardProject(edit_id)}
                      disabled={
                        editLoading ||
                        saveDraftLoading ||
                        saveAndPublishLoading ||
                        discardProjectLoading
                      }
                      className={` font-medium text-xl mlg:text-2xl px-5 text-red-500 rounded-xl h-fit mt-1 `}
                    >
                      {discardProjectLoading ? (
                        <Oval
                          height={35}
                          width={35}
                          color="#8538FF"
                          wrapperStyle={{}}
                          wrapperClass=""
                          visible={true}
                          ariaLabel="oval-loading"
                          secondaryColor="#8538FF"
                          strokeWidth={2}
                          strokeWidthSecondary={2}
                        />
                      ) : (
                        "Discard"
                      )}
                    </button>
                  )}{" "}
              </div>
              <div className="max-mlg:w-full flex flex-col">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={handleProjectPreviewClick}
                    className={`h-10 w-full mlg:w-60 bg-primary-green flex justify-center items-center text-white mlg:text-lg font-medium rounded-lg disabled:cursor-not-allowed disabled:opacity-70 
              `}
                  >
                    Preview
                  </button>

                  {activeStep === 2 && (
                    <button
                      onClick={() => setIsPublish(true)}
                      type="submit"
                      disabled={submitLoading?.isLoading}
                      className="w-full mlg:w-60 h-10 bg-light-primary flex justify-center items-center min-w-[80px] text-white mlg:text-lg font-semibold  rounded-lg disabled:cursor-not-allowed disabled:opacity-70 "
                    >
                      {saveAndPublishLoading && submitLoading?.isLoading ? (
                        <Bars
                          height="25"
                          width="25"
                          color="#ffffff"
                          ariaLabel="bars-loading"
                          wrapperStyle={{}}
                          wrapperClass=""
                          visible={true}
                        />
                      ) : projectData?.project &&
                        projectData?.project?.visible ? (
                        "Save changes"
                      ) : (
                        "Publish"
                      )}
                    </button>
                  )}
                </div>
                {activeStep === 2 && !projectData?.project?.visible ? (
                  <>
                    <div className="flex mt-5 w-full items-start mlg:max-w-[31rem]">
                      <input
                        type="checkbox"
                        id="termsOfService"
                        checked={formik.values.termsOfService}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "termsOfService",
                            e.target.checked
                          )
                        }
                        className=" mt-1 mr-1 accent-primary rounded-sm  "
                      />
                      <label htmlFor="termsOfService" className=" text-sm">
                        By publishing my project on FunDeSci as an NFT, I agree
                        to the{" "}
                        <ExternalLink
                          className="underline"
                          href="/terms-of-service"
                        >
                          terms of service
                        </ExternalLink>{" "}
                        and commit to following the rules set forth by the
                        platform.
                      </label>
                    </div>
                    {formik &&
                      formik.touched?.termsOfService &&
                      formik.errors?.termsOfService && (
                        <span className="text-red-500 text-sm mt-1">
                          {formik.errors?.termsOfService}
                        </span>
                      )}
                  </>
                ) : null}
              </div>
            </div>
          </form>
        </Container>

        {showProjectPreview && (
          <ProjectPreview
            previewData={projectPreviewData}
            setShow={setShowProjectPreview}
            show={showProjectPreview}
          />
        )}
      </div>
    );
};

export default CreateProject;
